import { LegalPageApp, LegalPageType } from 'generated/graphql';

/**
 * App identifiers used in the consumer-legal routes. These are the generated
 * `LegalPageApp` GraphQL enum values (kept as plain strings for URL params and
 * picker state). Both Tock and JCE pages are localized and require a locale.
 */
export type LegalApp = LegalPageApp;

export interface AppOption {
  value: LegalApp;
  label: string;
}

export const APP_OPTIONS: AppOption[] = [
  { value: 'TOCK', label: 'Tock (exploretock.com)' },
  { value: 'JCE', label: 'JCE (Global Dining)' },
];

export const APP_LABELS: Record<LegalApp, string> = {
  TOCK: 'Tock',
  JCE: 'JCE',
};

/** Every consumer legal page (Tock and JCE alike) is authored per locale. */
export interface LocaleOption {
  value: string;
  label: string;
}

export const LOCALES: LocaleOption[] = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'fr-CA', label: 'Français (CA)' },
];

export const DEFAULT_LOCALE = LOCALES[0].value;

/**
 * Display labels using proper legal jargon. The underlying enum KEYS are
 * unchanged (URL params, query variables, and `data-testid`s all use the raw
 * key); only the human-readable text lives here. Exhaustive over `LegalPageType`
 * so a new enum value is a compile error until it is labeled.
 */
export const TYPE_LABELS: Record<LegalPageType, string> = {
  TERMS_OF_USE: 'Terms of Service',
  PAYMENT_TERMS: 'Payment Terms',
  PRIVACY_POLICY: 'Privacy Policy',
  PRIVACY_SPECIFIC: 'Supplemental Privacy Notices',
  COOKIE_POLICY: 'Cookie Policy',
  MERCHANT_DPA: 'Data Processing Addendum',
  MERCHANT_TERMS: 'Merchant Terms',
  AFFILIATE: 'Affiliate Program Terms',
  ACCESSIBILITY_STATEMENT: 'Accessibility Statement',
};

/** Every known type, in category order. Used to validate editor route params. */
export const TYPE_ORDER: LegalPageType[] = [
  'TERMS_OF_USE',
  'PAYMENT_TERMS',
  'PRIVACY_POLICY',
  'PRIVACY_SPECIFIC',
  'COOKIE_POLICY',
  'MERCHANT_DPA',
  'MERCHANT_TERMS',
  'AFFILIATE',
  'ACCESSIBILITY_STATEMENT',
];

// ─── Category taxonomy ───────────────────────────────────────────────────────

export const LEGAL_CATEGORY = {
  END_USER_AGREEMENTS: 'END_USER_AGREEMENTS',
  PRIVACY_DATA_PROTECTION: 'PRIVACY_DATA_PROTECTION',
  MERCHANT_PARTNER_AGREEMENTS: 'MERCHANT_PARTNER_AGREEMENTS',
  PROGRAM_SPECIFIC_TERMS: 'PROGRAM_SPECIFIC_TERMS',
  COMPLIANCE_STATEMENTS: 'COMPLIANCE_STATEMENTS',
} as const;

export type LegalCategory = (typeof LEGAL_CATEGORY)[keyof typeof LEGAL_CATEGORY];

export const CATEGORY_ORDER: LegalCategory[] = [
  LEGAL_CATEGORY.END_USER_AGREEMENTS,
  LEGAL_CATEGORY.PRIVACY_DATA_PROTECTION,
  LEGAL_CATEGORY.MERCHANT_PARTNER_AGREEMENTS,
  LEGAL_CATEGORY.PROGRAM_SPECIFIC_TERMS,
  LEGAL_CATEGORY.COMPLIANCE_STATEMENTS,
];

export const CATEGORY_LABELS: Record<LegalCategory, string> = {
  END_USER_AGREEMENTS: 'End-User Agreements',
  PRIVACY_DATA_PROTECTION: 'Privacy & Data Protection',
  MERCHANT_PARTNER_AGREEMENTS: 'Merchant / Partner Agreements',
  PROGRAM_SPECIFIC_TERMS: 'Program-Specific Terms',
  COMPLIANCE_STATEMENTS: 'Compliance Statements',
};

/** Every type maps to exactly one category. Exhaustive over `LegalPageType`. */
export const TYPE_CATEGORY: Record<LegalPageType, LegalCategory> = {
  TERMS_OF_USE: LEGAL_CATEGORY.END_USER_AGREEMENTS,
  PAYMENT_TERMS: LEGAL_CATEGORY.END_USER_AGREEMENTS,
  PRIVACY_POLICY: LEGAL_CATEGORY.PRIVACY_DATA_PROTECTION,
  PRIVACY_SPECIFIC: LEGAL_CATEGORY.PRIVACY_DATA_PROTECTION,
  COOKIE_POLICY: LEGAL_CATEGORY.PRIVACY_DATA_PROTECTION,
  MERCHANT_DPA: LEGAL_CATEGORY.PRIVACY_DATA_PROTECTION,
  MERCHANT_TERMS: LEGAL_CATEGORY.MERCHANT_PARTNER_AGREEMENTS,
  AFFILIATE: LEGAL_CATEGORY.PROGRAM_SPECIFIC_TERMS,
  ACCESSIBILITY_STATEMENT: LEGAL_CATEGORY.COMPLIANCE_STATEMENTS,
};

/**
 * Per-app applicability, in category order. The landing iterates the selected
 * app's list (grouped by category) rather than the flat `TYPE_ORDER`, so each
 * app only shows the legal pages it actually publishes.
 */
export const APP_TYPES: Record<LegalApp, LegalPageType[]> = {
  TOCK: [
    'TERMS_OF_USE',
    'PRIVACY_POLICY',
    'PRIVACY_SPECIFIC',
    'COOKIE_POLICY',
    'MERCHANT_DPA',
    'MERCHANT_TERMS',
    'AFFILIATE',
  ],
  JCE: [
    'TERMS_OF_USE',
    'PAYMENT_TERMS',
    'PRIVACY_POLICY',
    'COOKIE_POLICY',
    'MERCHANT_DPA',
    'ACCESSIBILITY_STATEMENT',
  ],
};

// ─── Tag colors ──────────────────────────────────────────────────────────────

/** Pantry Tag colors keyed by the server page state string. */
export type LegalStateColor = 'primary' | 'secondary' | 'success' | 'warning' | 'danger';

export const STATE_TAG_COLORS: Record<string, LegalStateColor> = {
  DRAFT: 'secondary',
  PENDING_REVIEW: 'warning',
  APPROVED: 'primary',
  PUBLISHED: 'success',
  ARCHIVED: 'secondary',
};

export function stateTagColor(state: string): LegalStateColor {
  return STATE_TAG_COLORS[state] ?? 'secondary';
}

/** Distinct, high-salience Tag colors per app so the active app is unmistakable. */
export const APP_TAG_COLORS: Record<LegalApp, LegalStateColor> = {
  TOCK: 'primary',
  JCE: 'success',
};

// ─── Derived per-type status ─────────────────────────────────────────────────

/** Single, human-readable status shown per type on the landing. */
export type DisplayStatus = 'Published' | 'In review' | 'Draft' | 'Not created';

export interface TypeStatus {
  status: DisplayStatus;
  color: LegalStateColor;
  /**
   * Secondary indicator shown alongside a live "Published" status when there is
   * also in-flight work (a new draft or something in review) for the same type.
   */
  pending?: { label: 'In review' | 'Draft'; color: LegalStateColor };
}

/**
 * Collapses the current (non-archived) rows for a single type into one display
 * status. A live page reads as the primary "Published" status; any in-flight
 * work (In review for PENDING_REVIEW/APPROVED, else Draft) surfaces as a
 * secondary `pending` badge so editors still see there is something to finish.
 * With nothing published, the in-flight work becomes the primary status:
 * In review → Draft → Not created.
 */
export function deriveTypeStatus(rows: readonly { state: string }[]): TypeStatus {
  const states = new Set(rows.map((row) => row.state));
  const inReview = states.has('PENDING_REVIEW') || states.has('APPROVED');
  const hasDraft = states.has('DRAFT');

  if (states.has('PUBLISHED')) {
    let pending: TypeStatus['pending'];
    if (inReview) {
      pending = { label: 'In review', color: 'warning' };
    } else if (hasDraft) {
      pending = { label: 'Draft', color: 'secondary' };
    }
    return { status: 'Published', color: 'success', pending };
  }
  if (inReview) {
    return { status: 'In review', color: 'warning' };
  }
  if (hasDraft) {
    return { status: 'Draft', color: 'secondary' };
  }
  return { status: 'Not created', color: 'secondary' };
}

/** Formats an instant as a CST calendar date (e.g. "Jan 3, 2026"), matching the consumer render. */
function formatCentralDate(iso: string | null | undefined): string | null {
  if (!iso) {
    return null;
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Chicago',
  }).format(date);
}

/** Formats an effective-date instant as a CST calendar date, matching the consumer render. */
export function formatEffectiveDate(iso: string | null | undefined): string | null {
  return formatCentralDate(iso);
}

/** Formats a published-at instant as a CST calendar date for the dashboard cell. */
export function formatPublishedAt(iso: string | null | undefined): string | null {
  return formatCentralDate(iso);
}

export function typeLabel(type: string): string {
  return TYPE_LABELS[type as LegalPageType] ?? type;
}

/** Human-readable app label for a legal page, from its generated enum value. */
export function appLabelOf(page: { app: LegalPageApp }): string {
  return page.app === LegalPageApp.Jce ? APP_LABELS.JCE : APP_LABELS.TOCK;
}

/** Parses a URL/picker app string into a known LegalApp, defaulting to TOCK. */
export function parseApp(raw: string | undefined): LegalApp {
  return raw?.toUpperCase() === 'JCE' ? 'JCE' : 'TOCK';
}

/** URL segment for an app (lowercased). */
export function appParam(app: LegalApp): string {
  return app.toLowerCase();
}

export function isKnownType(type: string | undefined): type is LegalPageType {
  return !!type && (TYPE_ORDER as string[]).includes(type);
}

/**
 * Kebab-case URL segment for a legal page type (e.g. `TERMS_OF_USE` ->
 * `terms-of-use`). Enum keys are still used for GraphQL vars and `data-testid`s;
 * only the URL segment is kebab-cased.
 */
export function typeSlug(type: LegalPageType): string {
  return type.toLowerCase().replaceAll('_', '-');
}

/**
 * Parses a kebab-case URL slug back into a known `LegalPageType`, or `undefined`
 * if it does not match any known type.
 */
export function parseTypeSlug(slug: string | undefined): LegalPageType | undefined {
  if (!slug) {
    return undefined;
  }
  const candidate = slug.toUpperCase().replaceAll('-', '_');
  return isKnownType(candidate) ? candidate : undefined;
}
